/**
 * The audio seam (doc 18 §1) — the one place that knows which sim event makes
 * which noise, and where on screen it happened.
 *
 * The audio layer knows nothing about the game; the game knows nothing about
 * WebAudio. This file is the handshake, and it is deliberately a flat switch:
 * when a sound is wrong, it is wrong on one line here.
 */

import { audio, type SoundId } from '../audio/engine.ts';
import type { Camera } from '../render/camera.ts';
import { TH, TW } from '../render/iso.ts';
import type { SimEvent } from '../sim/types.ts';

export interface SoundStage {
  camera: Camera;
  viewport: { cssW: number; cssH: number };
}

/** Where a tile-space point lands on screen, for the stereo pan (doc 17 §4). */
function screenX(stage: SoundStage, x: number, y: number): number {
  return stage.camera.worldToScreen(Math.round((x - y) * TW), Math.round((x + y) * TH + TH)).x;
}

/**
 * One drained batch → one round of noise. Returns true if anything worth
 * ducking the ambience for happened (a banner-class event).
 */
export function playEvents(events: SimEvent[], stage: SoundStage): boolean {
  const w = stage.viewport.cssW;
  let loud = false;
  const at = (id: SoundId, x: number, y: number, gain?: number): void =>
    audio.play(id, { x: screenX(stage, x, y), width: w, ...(gain === undefined ? {} : { gain }) });

  for (const ev of events) {
    switch (ev.type) {
      case 'shot':
        at('shot', ev.x, ev.y);
        break;
      case 'chain':
        if (ev.points[0]) at('chain', ev.points[0].x, ev.points[0].y);
        break;
      case 'launch':
        at('launch', ev.fromX, ev.fromY);
        break;
      case 'explosion':
        at('explosion', ev.x, ev.y, Math.min(1, 0.6 + ev.radius / 8));
        break;
      case 'spit':
        at('spit', ev.x, ev.y);
        break;
      case 'bite':
        at('bite', ev.x, ev.y);
        break;
      case 'structureDestroyed':
        audio.play('structure_lost');
        break;
      case 'placed':
        at('place', ev.i, ev.j);
        break;
      case 'upgraded':
        audio.play('upgrade');
        break;
      case 'pulled':
      case 'autoLure':
        audio.play('lure');
        loud = true;
        break;
      case 'lure':
        at('lure', ev.x, ev.y, 0.5);
        break;
      case 'riled':
        audio.play('riled');
        loud = true;
        break;
      case 'waveCleared':
        audio.play('payday');
        loud = true;
        break;
      case 'brownout':
        if (ev.on) audio.play('brownout');
        break;
      case 'grab':
      case 'pulse':
      case 'enrage':
        audio.play('boss');
        loud = true;
        break;
      case 'act2':
        audio.play('boss');
        loud = true;
        break;
      case 'victory':
        audio.play('victory');
        loud = true;
        break;
      case 'defeat':
        audio.play('defeat');
        loud = true;
        break;
      // dive, thread, cast, enemyHit, enemyDied and rejected are either constant
      // (a wave of deaths would be a wall) or already carried by another sound
      default:
        break;
    }
  }
  return loud;
}
